/**
 * Mid-run game state saved to the player's own PDS (collection
 * com.equalnotequals.tanks.gamestate, rkey "self") so they can close the window
 * and resume the climb later — even on another machine. One record per player;
 * overwritten at each layer checkpoint, deleted on game over / fresh start.
 *
 * Storage hygiene: a save older than ONE WEEK is treated as expired — it's
 * deleted from the repo on load and ignored.
 */

import { type Session, readRecord, writeRecord, deleteRecord } from "./atproto";
import { log } from "../engine/debug";

export const GAMESTATE_NSID = "com.equalnotequals.tanks.gamestate";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // one week

export interface SavedGameState {
  savedAt: string;
  /** the layer the player will play when they resume */
  layer: number;
  /** CARD MODE: banked cash carried into the resumed floor */
  cash?: number;
  /** lives remaining */
  lives: number;
  /** total tanks destroyed this run */
  kills: number;
  /** accumulated points this run (distance + bounce scoring) */
  score?: number;
  /** dev mode was used — the run stays progress-disabled after resume */
  tainted: boolean;
  /** roguelike upgrade picks this run: upgrade id -> times taken (replayed on resume) */
  upgrades?: Record<string, number>;
  /** the current layer's bonus pickup was already collected (don't re-grant on resume) */
  pickupTaken?: boolean;
  /** hired mercenaries currently alive (re-spawned near the player on resume) */
  allies?: number;
  /** total mercenaries ever bought this run (drives the escalating shop price) */
  alliesBought?: number;
  /** the "reinforcements" upgrade was bought — dead mercs redeploy mid-floor */
  mercRespawn?: boolean;
  /** the round-end shop was open + the free perk un-picked when saved (re-offer on resume) */
  shopPending?: boolean;
}

// Each game mode has its own save slot (rkey) so a normal climb + a card-mode run coexist.
export type SaveSlot = "self" | "cardmode";

/** Load the saved run for a slot, enforcing the one-week TTL (stale saves are deleted). */
export async function loadGameState(s: Session, slot: SaveSlot = "self"): Promise<SavedGameState | null> {
  const rec = await readRecord<SavedGameState>(s.did, GAMESTATE_NSID, slot);
  if (!rec || typeof rec.layer !== "number" || rec.layer < 1) return null;
  const age = Date.now() - Date.parse(rec.savedAt ?? "");
  if (!(age >= 0 && age < TTL_MS)) {
    log.info("gamestate", "saved game expired (>1 week) — deleting");
    void clearGameState(s, slot);
    return null;
  }
  return rec;
}

/** Overwrite the checkpoint (best-effort — never throws into the game loop). */
export async function saveGameState(s: Session, state: SavedGameState, slot: SaveSlot = "self"): Promise<void> {
  try {
    await writeRecord(s, GAMESTATE_NSID, slot, state as unknown as Record<string, unknown>);
    log.debug("gamestate", `checkpoint saved (${slot} layer ${state.layer})`);
  } catch {
    /* the next checkpoint will retry */
  }
}

/** Remove the save (game over / fresh start). Missing record is fine. */
export async function clearGameState(s: Session, slot: SaveSlot = "self"): Promise<void> {
  try {
    await deleteRecord(s, GAMESTATE_NSID, slot);
  } catch {
    /* nothing to delete */
  }
}
